/**
 * submit-event-followup.js
 * Domain use case for handling Event follow-up submissions.
 */

export function createSubmitEventFollowup({
  sendFollowup,
  buildFollowupPrompt,
  loadTeamContext = defaultLoadTeamContext,
  mapTeamTypeToLens = defaultMapTeamTypeToLens,
  sanitizeResponse = (response) => response,
  metricsLogger = null,
  metricsStage = 'submitEventFollowup',
  now = defaultNow,
} = {}) {
  if (typeof sendFollowup !== 'function') {
    throw new Error('sendFollowup is required');
  }
  if (typeof buildFollowupPrompt !== 'function') {
    throw new Error('buildFollowupPrompt is required');
  }

  return async function submitEventFollowup({
    question,
    eventContext = {},
    persona = 'Event Facilitator',
    lens = 'general',
    teamId = null,
    // An ART: the subject is a scope, named by the caller since it has no team record.
    scopeId = null,
    teamName = null,
    attachments = [],
    usePrecision = true,
    useIntelligenceContext = false,
  } = {}) {
    const trimmedQuestion = normalizeString(question);
    if (!trimmedQuestion) {
      throw new Error('question is required');
    }

    const files = normalizeAttachments(attachments);
    const normalizedEventContext = normalizeEventContext(eventContext);

    const teamContext = await safeLoadTeamContext(loadTeamContext, teamId);
    const effectiveLens = resolveLens(lens, teamContext.teamType, mapTeamTypeToLens);

    const followupMessage = usePrecision === false
      ? trimmedQuestion
      : buildFollowupMessage(buildFollowupPrompt, {
          usePrecision,
          followupQuestion: trimmedQuestion,
          eventContext: normalizedEventContext,
          persona,
          lens: effectiveLens,
          teamId,
          attachments: files,
          teamInstructions: teamContext.teamInstructions,
          teamType: teamContext.teamType,
        }) || trimmedQuestion;

    const payloadBytes = estimatePayloadBytes(files);
    const submitStart = now();
    const response = await sendFollowup({
      persona,
      lens: effectiveLens,
      teamId,
      scopeId,
      teamName: teamContext.teamName || normalizeString(teamName) || null,
      useIntelligenceContext: useIntelligenceContext === true,
      followupMessage,
      originalQuestion: trimmedQuestion,
      originalMessage: trimmedQuestion,
      eventContext: normalizedEventContext,
      attachments: files,
      usePrecision,
      teamContext,
      payloadBytes,
    });
    const submitDuration = elapsedSince(submitStart, now);

    if (typeof metricsLogger === 'function') {
      safeInvoke(metricsLogger, {
        stage: metricsStage,
        durationMs: submitDuration,
        teamFetchMs: teamContext.durationMs ?? null,
        payloadBytes,
      });
    }

    const sanitizedResponse = sanitizeResponse(response);

    const timings = {
      teamFetchMs: teamContext.durationMs ?? null,
      submitMs: submitDuration,
    };

    return {
      response: sanitizedResponse,
      precisionMessage: followupMessage,
      context: {
        question: trimmedQuestion,
        lens: effectiveLens,
        teamInstructions: teamContext.teamInstructions ?? null,
        teamType: teamContext.teamType ?? null,
        teamName: teamContext.teamName ?? null,
      },
      durationMs: submitDuration,
      timings,
      payloadBytes,
      tokens: response?.tokens || response?.usage?.total_tokens || null,
    };
  };
}

function buildFollowupMessage(builder, {
  usePrecision,
  followupQuestion,
  eventContext,
  persona,
  lens,
  teamId,
  attachments,
  teamInstructions,
  teamType,
}) {
  if (typeof builder !== 'function') {
    return followupQuestion;
  }

  if (builder.length >= 2) {
    return builder(
      followupQuestion,
      eventContext,
      {
        persona,
        lens,
        teamId,
        files: attachments,
        teamInstructions,
        teamType,
        usePrecision,
      }
    );
  }

  return builder({
    usePrecision,
    followupQuestion,
    eventContext,
    persona,
    lens,
    teamId,
    attachments,
    teamInstructions,
    teamType,
  });
}

function normalizeEventContext(context = {}) {
  return {
    event: context.event ?? context.eventType ?? null,
    timebox: context.timebox ?? null,
    notes: context.notes ?? null,
  };
}

function normalizeAttachments(value) {
  if (Array.isArray(value)) {
    return value.slice();
  }
  if (!value) {
    return [];
  }
  if (typeof value[Symbol.iterator] === 'function') {
    return Array.from(value);
  }
  return [value];
}

async function safeLoadTeamContext(loadTeamContext, teamId) {
  try {
    const result = await loadTeamContext(teamId);
    if (result && typeof result === 'object') {
      return {
        teamInstructions: result.teamInstructions ?? null,
        teamType: result.teamType ?? null,
        teamName: result.teamName ?? null,
        durationMs: result.durationMs ?? null,
      };
    }
  } catch (error) {
    // fall through to defaults
  }
  return {
    teamInstructions: null,
    teamType: null,
    teamName: null,
    durationMs: null,
  };
}

function resolveLens(currentLens = 'general', teamType = null, mapper = defaultMapTeamTypeToLens) {
  if (!teamType) {
    return currentLens || 'general';
  }
  if (currentLens && currentLens !== 'general' && currentLens !== 'auto') {
    return currentLens;
  }
  return mapper(teamType) || (typeof teamType === 'string' ? teamType.toLowerCase() : currentLens || 'general');
}

function defaultMapTeamTypeToLens(teamType) {
  if (!teamType) {
    return null;
  }

  return TEAM_TYPE_TO_LENS[teamType] || (typeof teamType === 'string' ? teamType.toLowerCase() : null);
}

function normalizeString(value) {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
}

function defaultLoadTeamContext() {
  return {
    teamInstructions: null,
    teamType: null,
    teamName: null,
    durationMs: null,
  };
}

function defaultNow() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

function elapsedSince(start, nowFn = defaultNow) {
  const end = nowFn();
  return Number.isFinite(start) ? Math.max(0, end - start) : undefined;
}

function estimatePayloadBytes(files) {
  if (!Array.isArray(files) || !files.length) {
    return 0;
  }
  return files.reduce((total, file) => total + (file?.size || 0), 0);
}

function safeInvoke(fn, payload) {
  try {
    fn(payload);
  } catch (error) {
    // ignore metrics errors
  }
}

const TEAM_TYPE_TO_LENS = {
  Scrum: 'scrum',
  Kanban: 'kanban',
  SAFe: 'safe',
  LeSS: 'less',
  XP: 'xp',
  Agile: 'agile',
};

if (typeof window !== 'undefined') {
  window.AgileProDomain = window.AgileProDomain || {};
  window.AgileProDomain.event = window.AgileProDomain.event || {};
  window.AgileProDomain.event.createSubmitEventFollowup = createSubmitEventFollowup;
}
